"use client"

/**
 * Re-validate the session on a back/forward cache restore (design §1.6).
 *
 * The browser replays a ``bfcache`` snapshot without re-running React, so
 * a page restored via back/forward can show stale authenticated state
 * (or, with the new gate, a captured loader). This listens for
 * ``pageshow`` and, when ``event.persisted`` is true (a genuine bfcache
 * restore), invalidates the ``/api/auth/me/`` query. Live React then
 * refetches and ``AuthGate`` re-resolves to children or — via the global
 * 401 handler — a redirect.
 *
 * Renders nothing; mounted once inside ``Providers``.
 */

import { useEffect } from "react"
import { useQueryClient } from "@tanstack/react-query"

import { currentUserKey } from "@/features/auth/hooks"

export function BfcacheRevalidator() {
  const queryClient = useQueryClient()

  useEffect(() => {
    function handlePageShow(event: PageTransitionEvent) {
      if (event.persisted) {
        queryClient.invalidateQueries({ queryKey: currentUserKey })
      }
    }
    window.addEventListener("pageshow", handlePageShow)
    return () => {
      window.removeEventListener("pageshow", handlePageShow)
    }
  }, [queryClient])

  return null
}
